import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  X,
  XCircle,
  AlertCircle,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { useAuthStore } from '@/stores/authStore';

interface RejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
  type: 'hr' | 'it';
  onSuccess: () => void;
}

export default function RejectModal({ isOpen, onClose, formId, type, onSuccess }: RejectModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const rejectMutation = useMutation({
    mutationFn: async () => {
      const endpoint = type === 'hr' 
        ? `/access-forms/${formId}/reject-hr`
        : `/access-forms/${formId}/reject-it`;
      
      const payload = {
        reason,
        rejectorName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
        rejectorId: user?.id,
      };

      const response = await api.post(endpoint, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessForm', formId] });
      queryClient.invalidateQueries({ queryKey: ['accessForms'] });
      resetForm();
      onSuccess();
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to reject access form');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!reason.trim()) {
      setSubmitError('Please provide a reason for rejection');
      return;
    }

    rejectMutation.mutate();
  };

  const resetForm = () => {
    setReason('');
    setSubmitError(null);
  };

  if (!isOpen) return null;

  const title = type === 'hr' ? 'HR Rejection' : 'IT Rejection';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-lg w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            <h2 className="text-xl font-semibold">{title}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Error Alert */}
          {submitError && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <p className="text-destructive">{submitError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <XCircle className="w-5 h-5" />
                <p className="font-medium">Reject Access Request</p>
              </div>
              <p className="text-sm text-red-600 mt-1">
                This action will reject the access request. The requester will be notified.
              </p>
            </div>

            {/* Rejection Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">
                Reason for Rejection <span className="text-destructive">*</span>
              </Label>
              <textarea
                id="reason"
                className={cn(
                  "w-full min-h-[120px] p-3 rounded-md border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring",
                  !reason.trim() && submitError ? "border-destructive focus:ring-destructive" : "border-input"
                )}
                placeholder="Please explain why this access request is being rejected..."
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (submitError) setSubmitError(null);
                }}
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={rejectMutation.isPending}
            loading={rejectMutation.isPending}
            variant="destructive"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
}
